'use client';

import * as React from 'react';
import { format, startOfMonth } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { cn } from '@/utils/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { OnSelectHandler } from 'react-day-picker';
import { Field, FieldProps } from 'formik';

export type SingleDatePickerProps = {
  id?: string;
  className?: string;
  default?: Date | null;
  date?: Date;
  setDate?: OnSelectHandler<Date>;
  disabled?: boolean;
  pickerProps?: Omit<
    React.ComponentProps<typeof Calendar>,
    'mode' | 'required'
  >;
  placeholder?: string;
};

export const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  id,
  className,
  default: defaultDate = startOfMonth(new Date()),
  date: controlledDate,
  setDate: controlledSetDate,
  disabled,
  pickerProps,
  placeholder = 'Selecciona una fecha',
}) => {
  const [open, setOpen] = React.useState(false);
  const [uncontrolledDate, uncontrolledSetDate] = React.useState<
    Required<Date> | undefined
  >(defaultDate ?? undefined);

  const date = controlledDate ?? uncontrolledDate;
  const setDate = controlledSetDate ?? uncontrolledSetDate;

  const handleDateChange: OnSelectHandler<Date> = (...props) => {
    setDate(...props);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant={'outline'}
          className={cn(
            'sm:min-w-max justify-between text-left font-normal w-full',
            !date && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <div>
            {date ? format(date, 'dd LLL, y') : <span>{placeholder}</span>}
          </div>
          <CalendarIcon size={20} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          autoFocus
          mode="single"
          required
          selected={date}
          onSelect={handleDateChange}
          disabled={{ after: new Date() }}
          {...pickerProps}
        />
      </PopoverContent>
    </Popover>
  );
};

export type ConnectedSingleDatePickerProps = Omit<
  SingleDatePickerProps,
  'date' | 'setDate'
> & { name: string };

export const ConnectedSingleDatePicker: React.FC<
  ConnectedSingleDatePickerProps
> = ({ name, ...props }) => (
  <Field name={name}>
    {({ field, form }: FieldProps) => (
      <SingleDatePicker
        {...field}
        {...props}
        date={field.value}
        setDate={async (date) => {
          await form.setFieldValue(field.name, date, true);
          form.setFieldTouched(field.name, true, true);
        }}
      />
    )}
  </Field>
);

export const ConnectedSingleLocalDatePicker: React.FC<
  ConnectedSingleDatePickerProps
> = ({ name, ...props }) => (
  <Field name={name}>
    {({ field, form }: FieldProps) => {
      // Convert yyyy-MM-dd string to local Date if needed
      let dateValue: Date | null = null;
      if (
        typeof field.value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(field.value)
      ) {
        const [year, month, day] = field.value.split('-').map(Number);
        dateValue = new Date(year, month - 1, day);
      } else if (field.value instanceof Date) {
        dateValue = field.value;
      } else {
        dateValue = new Date(field.value);
      }

      return (
        <SingleDatePicker
          {...field}
          {...props}
          date={dateValue}
          setDate={(date) => {
            // Save as yyyy-MM-dd string in local time
            const formatted =
              date instanceof Date && !isNaN(date.getTime())
                ? format(date, 'yyyy-MM-dd')
                : '';
            form.setFieldValue(field.name, formatted);
            form.setFieldTouched(field.name, true);
          }}
        />
      );
    }}
  </Field>
);
